import React, { FunctionComponent, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  View,
  Text,
  StyleSheet,
  Linking,
  TouchableOpacity,
  useWindowDimensions,
} from "react-native";
import SignatureScreen, {
  SignatureViewRef,
} from "react-native-signature-canvas";
import { PRIVACY_URL, TERMS_URL } from "../../constants";
import { Store, Visit } from "../../types";
import { buildMedicalHistoryPayload } from "../../utils/medicalHistory";
import { ConsentFlowState, reset } from "../../state/consentFlow";
import { pick } from "lodash";
import * as FileSystem from "expo-file-system";

import Button from "../../components/Button";
import { colors } from "../../theme";
import { useDispatch, useSelector } from "react-redux";
import StickyFooter from "../../components/StickyFooter";
import CheckBox from "../../components/CheckBox";
import Heading from "../../components/Heading";
import { CREATE_VISIT, UPDATE_VISIT } from "./gql";
import { uploadBlob } from "../../utils/presignedUpload";
import { RootState } from "../../state";

type Params = {
  store: Store;
  visit?: Visit;
};

function buildPayloadFromConsentState(state: ConsentFlowState) {
  const consentAnswers = Object.keys(state.answeredConsentQuestions).map(
    (questionId) => state.answeredConsentQuestions[questionId],
  );
  return {
    customerId: state.subject.id,
    medicalHistory: buildMedicalHistoryPayload(
      state.medicalHistory,
      state.medicalConcerns,
    ),
    consentAnswers,
    services: Object.keys(state.services).map((key) => {
      const service = state.services[key];

      // For existing services we always want to also send over the id
      // but we can't use the id field to verify because the id is present
      // on all services (it is generated on the client for new services)
      const fields = service.isExistingService
        ? ["id", "serviceId", "submittedInformation"]
        : ["serviceId", "submittedInformation"];

      return pick(service, fields);
    }),
  };
}

interface SignatureCaptureProps {
  route: any;
  navigation: any;
  isUnscheduled: boolean;
}

const SignatureCapture: FunctionComponent<SignatureCaptureProps> = ({
  route,
  navigation,
  isUnscheduled,
}) => {
  const ref = useRef<SignatureViewRef>(null);
  const { width, height } = useWindowDimensions();
  const signatureWidth = Math.max(280, Math.min(width - 40, 560));
  const signatureHeight = height < 600 ? 120 : 180;
  const { store, visit }: Params = route.params;
  const dispatch = useDispatch();
  const [validSignature, setValidSignature] = useState(false);
  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const client = useApolloClient();
  const consentState = useSelector((state: RootState) => state.consentFlow);

  const handleSignature = async (signature: any) => {
    try {
      setSubmitError(null);
      setIsUploading(true);
      const localFilePath = await saveToFileSystem(signature);
      const response = await fetch(localFilePath);
      const blob = await response.blob();
      const signatureUrl = await uploadBlob(
        client,
        blob,
        "customer-signatures",
      );
      submitConsentInfo({
        ...buildPayloadFromConsentState(consentState),
        ...(visit ? { visitId: visit.id } : { storeId: store.id }),
        customerSignatureImageUrl: signatureUrl,
        unscheduled: isUnscheduled,
        consentAnsweredAt: new Date(),
      });
    } catch (_error) {
      setIsUploading(false);
      setSubmitError("The signature could not be prepared. Please try again.");
    }
  };

  const handleClear = () => {
    ref.current?.clearSignature();
    setValidSignature(false);
  };

  const handleEnd = () => {
    setValidSignature(true);
  };

  const handleConfirm = () => {
    if (validSignature && hasAcceptedAgreement) {
      ref.current?.readSignature();
    }
  };

  const saveToFileSystem: (signature: any) => Promise<string> = async (
    signature: any,
  ) => {
    const localImagepath = FileSystem.cacheDirectory + "signature.png";
    await FileSystem.writeAsStringAsync(
      localImagepath,
      signature.replace("data:image/png;base64,", ""),
      {
        encoding: FileSystem.EncodingType.Base64,
      },
    );
    return localImagepath;
  };

  const style = `.m-signature-pad--footer {display: none; margin: 0px;}`;

  const [createOrUpdateVisit] = useMutation(
    visit ? UPDATE_VISIT : CREATE_VISIT,
    {
      onCompleted: ({ result }) => {
        setIsUploading(false);
        if (result.visit) {
          dispatch(reset());
          navigation.replace("VisitConfirmed", {
            visitId: result.visit.id,
          });
        } else {
          setSubmitError("We could not submit your visit. Please try again.");
        }
      },
      onError: () => {
        setIsUploading(false);
        setSubmitError("We could not submit your visit. Please try again.");
      },
    },
  );

  const submitConsentInfo = (info: any) => {
    createOrUpdateVisit({
      variables: { input: info },
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.headingRow}>
          <View>
            <Heading size="l" style={styles.heading}>
              Add your signature
            </Heading>
            <Text style={styles.supportingText}>
              Use your finger to sign below.
            </Text>
          </View>
          <Text style={styles.finalStep}>Final step</Text>
        </View>

        <Text style={styles.contextText}>
          Signing for {store.name} - Visit consent
        </Text>

        <View
          style={[
            styles.signatureCard,
            { width: signatureWidth, height: signatureHeight + 74 },
          ]}
        >
          <View style={styles.signatureCardHeader}>
            <Text
              style={[
                styles.signatureStatus,
                validSignature && styles.signatureStatus__valid,
              ]}
            >
              {validSignature ? "Signature added" : "Signature required"}
            </Text>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={handleClear}
              style={styles.clearButton}
            >
              <Text style={styles.clearButtonText}>Clear</Text>
            </TouchableOpacity>
          </View>
          <View style={{ height: signatureHeight, width: signatureWidth - 32 }}>
            <SignatureScreen
              ref={ref}
              onOK={handleSignature}
              onEnd={handleEnd}
              webStyle={style}
              androidHardwareAccelerationDisabled={true}
            />
            <View style={styles.signatureLineContainer}>
              <Text style={styles.xCharacter}>X</Text>
              <View style={styles.signatureLine} />
            </View>
          </View>
        </View>
      </View>

      <StickyFooter>
        <View style={styles.footerContent}>
          <CheckBox
            isChecked={hasAcceptedAgreement}
            onPress={setHasAcceptedAgreement}
          >
            <Text style={styles.agreementLabel}>
              I agree this is my electronic signature.
            </Text>
          </CheckBox>

          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setShowAgreement(!showAgreement)}
            style={styles.disclosureButton}
          >
            <Text style={styles.disclosureButtonText}>
              Electronic signature agreement {showAgreement ? "-" : "+"}
            </Text>
          </TouchableOpacity>

          {showAgreement && (
            <View style={styles.disclosureContent}>
              <Text style={styles.conditionText}>
                My submitted signature is legally binding like a handwritten
                signature, and I consent to receive information electronically.
              </Text>
              <View style={styles.linkRow}>
                <Text
                  accessibilityRole="link"
                  onPress={() => Linking.openURL(TERMS_URL)}
                  style={styles.conditionText__Link}
                >
                  Terms of service
                </Text>
                <Text
                  accessibilityRole="link"
                  onPress={() => Linking.openURL(PRIVACY_URL)}
                  style={styles.conditionText__Link}
                >
                  Privacy policy
                </Text>
              </View>
            </View>
          )}

          {submitError && <Text style={styles.errorText}>{submitError}</Text>}

          <Button
            title="Sign and submit visit"
            onPress={handleConfirm}
            disabled={!validSignature || !hasAcceptedAgreement || isUploading}
            isLoading={isUploading}
          />
          <Text style={styles.submitHint}>
            {validSignature && hasAcceptedAgreement
              ? "Ready to submit securely."
              : "Add a signature and accept the agreement to continue."}
          </Text>
        </View>
      </StickyFooter>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.black,
    justifyContent: "space-between",
  },
  content: {
    width: "100%",
    maxWidth: 600,
    alignSelf: "center",
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  headingRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  heading: {
    color: colors.white,
  },
  supportingText: {
    color: colors.midGrey,
    marginTop: 4,
  },
  finalStep: {
    color: colors.black,
    backgroundColor: colors.yellow,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    fontSize: 12,
  },
  contextText: {
    color: colors.midGrey,
    fontSize: 12,
    marginTop: 12,
    marginBottom: 10,
  },
  signatureCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    alignSelf: "center",
    overflow: "hidden",
  },
  signatureCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 30,
  },
  signatureStatus: {
    color: colors.darkGrey,
    fontSize: 12,
  },
  signatureStatus__valid: {
    color: colors.statusGreen,
  },
  clearButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  clearButtonText: {
    color: colors.black,
    fontSize: 12,
    textDecorationLine: "underline",
  },
  signatureLineContainer: {
    position: "absolute",
    left: 8,
    right: 8,
    bottom: 12,
    flexDirection: "row",
    alignItems: "center",
    pointerEvents: "none",
  },
  xCharacter: {
    color: colors.black,
    fontSize: 18,
    marginRight: 8,
  },
  signatureLine: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
  },
  footerContent: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
  },
  agreementLabel: {
    color: colors.white,
    marginLeft: 12,
    paddingRight: 32,
  },
  disclosureButton: {
    marginTop: 12,
    marginBottom: 6,
  },
  disclosureButtonText: {
    color: colors.yellow,
    fontSize: 13,
  },
  disclosureContent: {
    marginBottom: 10,
  },
  conditionText: {
    color: colors.midGrey,
    fontSize: 12,
  },
  linkRow: {
    flexDirection: "row",
    marginTop: 6,
  },
  conditionText__Link: {
    color: colors.yellow,
    textDecorationLine: "underline",
    marginRight: 20,
    fontSize: 12,
  },
  errorText: {
    color: colors.red,
    textAlign: "center",
    marginBottom: 8,
    fontSize: 13,
  },
  submitHint: {
    color: colors.midGrey,
    textAlign: "center",
    marginTop: 6,
    fontSize: 12,
  },
});

export default SignatureCapture;
